import { c as _c } from "react/compiler-runtime";
import { OptimizedImage } from '@/components/images/optimized-image';
import { SettledSeal } from '@/components/market/settled-seal';
import { Badge } from '@/components/ui/app-badge';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { cn } from '@/lib/ui/utils';

// Leaf module for the shared outcome row/card sub-components. Lives apart from
// `other-outcomes-table` so `other-outcome-card` can use these without the
// table↔card import cycle (table → card → these parts).

interface OutcomeThumbProps {
  marketPubkey: string;
  index: number;
  label: string;
  imageUrl: string | null;
  imageNsfw: boolean;
  /** Tailwind size class, e.g. `size-8`. */
  size: string;
  showSettledBadge?: boolean;
  rankLabel?: string | undefined;
}

/** Outcome thumbnail (NSFW-aware, hue-tinted) shared by the desktop row + mobile card. */
export function OutcomeThumb(t0) {
  const $ = _c(24);
  const {
    marketPubkey,
    index,
    label,
    imageUrl,
    imageNsfw,
    size,
    showSettledBadge: t1,
    rankLabel
  } = t0;
  const showSettledBadge = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== index || $[1] !== label) {
    t2 = {
      title: label,
      index
    };
    $[0] = index;
    $[1] = label;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== marketPubkey) {
    t3 = {
      marketPubkey
    };
    $[3] = marketPubkey;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const colors = useOutcomeColor(t2, t3);
  let t4;
  if ($[5] !== colors.cta || $[6] !== size) {
    t4 = cn("relative flex shrink-0 items-center justify-center rounded-panel-inset", size, colors.cta);
    $[5] = colors.cta;
    $[6] = size;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = imageUrl ?? null;
  let t6;
  if ($[8] !== colors.hue || $[9] !== imageNsfw || $[10] !== index || $[11] !== marketPubkey || $[12] !== t5) {
    t6 = {
      kind: "outcome",
      marketPubkey,
      outcomeIndex: index,
      hue: colors.hue,
      imageUrl: t5,
      nsfw: imageNsfw
    };
    $[8] = colors.hue;
    $[9] = imageNsfw;
    $[10] = index;
    $[11] = marketPubkey;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== size || $[15] !== t6) {
    t7 = <OptimizedImage variant="thumbnail" entity={t6} alt="" size={size} rounded="rounded-panel-inset" preview={false} loadReveal="none" className="size-full object-cover" />;
    $[14] = size;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== rankLabel || $[18] !== showSettledBadge) {
    t8 = showSettledBadge ? <SettledSeal className="pointer-events-none absolute -bottom-1.5 -right-1.5 z-10 size-5" /> : rankLabel === undefined ? null : <Badge variant="secondary" className="absolute -bottom-1.5 -right-1.5 z-10">{rankLabel}</Badge>;
    $[17] = rankLabel;
    $[18] = showSettledBadge;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t4 || $[21] !== t7 || $[22] !== t8) {
    t9 = <span className={t4}>{t7}{t8}</span>;
    $[20] = t4;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
}